import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { SakuraField } from "../components/SakuraField";
import {
  accuracyPercent,
  charsPerMinute,
  createTypingState,
  guideParts,
  parsePromptQuery,
  remainingKana,
  typeKey,
  typedKana,
  type TypingState,
} from "../lib/romaji-typing";

// タイピング練習（/typing）。PC のキーボードでかなをローマ字で打つ（docs/spec.md 5-2・12章）。
// /typing?q=しんぶん,たべます は入口を飛ばしてすぐ始める（mode=custom）。

type Mode = "kana" | "words" | "custom";
type PromptsResponse = { prompts: string[]; words_available: boolean };
type SessionResult = {
  accuracy: number;
  chars_per_min: number;
  xp_awarded: number;
  coin_awarded: number;
  total_xp: number;
};
type Finished = { totalKeys: number; correctKeys: number; durationMs: number };

const KEY_RE = /^[a-z\-,.]$/i;

export function TypingPage() {
  // q が変わったら（同じ /typing 内の移動でも）最初からやり直す
  const location = useLocation();
  return <TypingRoot key={location.search} />;
}

function TypingRoot() {
  const [params] = useSearchParams();
  const fixed = parsePromptQuery(params.get("q"));
  const [session, setSession] = useState<{ mode: Mode; prompts: string[]; run: number } | null>(
    fixed ? { mode: "custom", prompts: fixed, run: 0 } : null,
  );

  if (!session) {
    return <TypingEntry onStart={(mode, prompts) => setSession({ mode, prompts, run: 0 })} />;
  }
  return (
    <TypingSession
      key={session.run}
      mode={session.mode}
      prompts={session.prompts}
      onAgain={(prompts) => setSession({ ...session, prompts, run: session.run + 1 })}
    />
  );
}

// ── 入口 ─────────────────────────────────────────────

function TypingEntry({ onStart }: { onStart: (mode: Mode, prompts: string[]) => void }) {
  const { t } = useTranslation(["common", "learner"]);
  const queryClient = useQueryClient();
  const [loadingMode, setLoadingMode] = useState<Mode | null>(null);
  const [error, setError] = useState(false);

  const { data: kana, isError } = useQuery({
    queryKey: ["typing-prompts", "kana"],
    queryFn: () => fetchPrompts("kana"),
    staleTime: 0,
    retry: false,
  });
  const wordsAvailable = kana?.words_available === true;

  async function start(mode: "kana" | "words") {
    setError(false);
    setLoadingMode(mode);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["typing-prompts", mode],
        queryFn: () => fetchPrompts(mode),
        staleTime: 0,
      });
      const usable = data.prompts.filter((p) => parsePromptQuery(p) !== null);
      if (usable.length === 0) throw new Error("no prompts");
      onStart(mode, usable);
    } catch {
      setError(true);
    } finally {
      setLoadingMode(null);
    }
  }

  return (
    <div className="relative w-full min-h-screen bg-gradient-to-br from-sakura/20 via-white to-blue-50 flex flex-col items-center px-4 py-8 overflow-hidden">
      <SakuraField />
      <div className="relative z-10 w-full max-w-xl flex items-center justify-between mb-8">
        <Link to="/" className="text-sm font-bold text-primary underline">{t("learner:back_to_map")}</Link>
      </div>
      <div className="relative z-10 w-full max-w-xl text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">{t("learner:typing_title")}</h1>
        <p className="text-sm sm:text-base text-muted mb-8">{t("learner:typing_lead")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ModeButton
            testId="typing-mode-kana"
            title={t("learner:typing_mode_kana")}
            sub={t("learner:typing_mode_kana_sub")}
            disabled={loadingMode !== null}
            loading={loadingMode === "kana"}
            onClick={() => start("kana")}
          />
          <ModeButton
            testId="typing-mode-words"
            title={t("learner:typing_mode_words")}
            sub={wordsAvailable ? t("learner:typing_mode_words_sub") : t("learner:typing_words_locked")}
            disabled={!wordsAvailable || loadingMode !== null}
            loading={loadingMode === "words"}
            onClick={() => start("words")}
          />
        </div>
        {(error || isError) && <p className="mt-6 text-sm text-accent">{t("learner:typing_load_error")}</p>}
      </div>
    </div>
  );
}

function fetchPrompts(mode: "kana" | "words") {
  return api<PromptsResponse>(`/api/learner/typing/prompts?mode=${mode}`);
}

function ModeButton(props: {
  testId: string; title: string; sub: string; disabled: boolean; loading: boolean; onClick: () => void;
}) {
  const { t } = useTranslation("common");
  return (
    <button
      type="button"
      data-testid={props.testId}
      disabled={props.disabled}
      onClick={props.onClick}
      className="bg-white rounded-2xl shadow-md border-2 border-transparent px-5 py-6 text-left transition hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-transparent"
    >
      <div className="text-lg font-bold text-primary">{props.loading ? t("loading") : props.title}</div>
      <div className="text-xs text-muted mt-1">{props.sub}</div>
    </button>
  );
}

// ── 練習中 ───────────────────────────────────────────

function TypingSession({ mode, prompts, onAgain }: { mode: Mode; prompts: string[]; onAgain: (prompts: string[]) => void }) {
  const { t } = useTranslation(["common", "learner"]);
  const [state, setState] = useState<TypingState>(() => createTypingState(prompts));
  const [missTick, setMissTick] = useState(0);
  const [imeHint, setImeHint] = useState(false);
  const [finished, setFinished] = useState<Finished | null>(null);
  const stateRef = useRef(state);
  const startedAt = useRef<number | null>(null);

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.isComposing || e.key === "Process") { setImeHint(true); return; }
    if (e.key.length !== 1 || !KEY_RE.test(e.key)) return;
    e.preventDefault();
    // 押しっぱなしの自動リピートは判定に入れない（誤キーが積み上がるのを防ぐ）
    if (e.repeat) return;
    const cur = stateRef.current;
    if (cur.done) return;
    if (startedAt.current === null) startedAt.current = performance.now();
    const { state: next, correct } = typeKey(cur, e.key);
    stateRef.current = next;
    setState(next);
    setImeHint(false);
    if (!correct) setMissTick((n) => n + 1);
    if (next.done) {
      const durationMs = Math.max(1, Math.round(performance.now() - (startedAt.current ?? performance.now())));
      setFinished({ totalKeys: next.totalKeys, correctKeys: next.correctKeys, durationMs });
    }
  }, []);

  // 開いた時点でキーを受け付ける（描画前に登録して、最初のキーを取りこぼさない）
  useLayoutEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  if (finished) {
    return <TypingComplete mode={mode} prompts={prompts} finished={finished} onAgain={onAgain} />;
  }

  const prompt = state.prompts[state.promptIndex];
  const typed = typedKana(state);
  const rest = [...prompt].slice([...typed].length).join("");
  const guide = guideParts(state);
  const progress = state.promptIndex / prompts.length;

  return (
    <div className="relative w-full min-h-screen bg-gradient-to-br from-sakura/10 via-white to-blue-50 flex flex-col">
      <header className="w-full max-w-3xl mx-auto flex items-center gap-4 px-4 pt-6">
        <Link to="/" aria-label={t("learner:back_to_map")} className="text-muted hover:text-primary text-2xl leading-none px-1">×</Link>
        <div className="flex-1 h-4 rounded-full bg-gray-200 overflow-hidden">
          <div className="h-full bg-sakura transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
        <div className="text-sm text-muted whitespace-nowrap">
          {t("learner:typing_question", { n: state.promptIndex + 1, total: prompts.length })}
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-24">
        <motion.div
          key={missTick}
          animate={missTick > 0 ? { x: [0, -8, 8, -4, 4, 0] } : undefined}
          transition={{ duration: 0.25 }}
          className="text-center"
        >
          <div data-testid="typing-prompt" lang="ja" className="font-ja text-5xl sm:text-6xl font-bold tracking-wide">
            <span data-testid="typing-typed" className="text-sakura">{typed}</span>
            <span className="text-primary">{rest}</span>
          </div>
          <div className="mt-5 font-mono text-xl sm:text-2xl tracking-wider select-none" aria-hidden="true">
            <span className="text-primary/70">{guide.typed}</span>
            <span className="text-gray-300">{guide.rest}</span>
          </div>
        </motion.div>

        <div className="mt-8 text-sm text-muted">
          {t("learner:typing_remaining_label")} <span data-testid="typing-remaining" className="font-bold text-primary">{remainingKana(state)}</span>
        </div>
        {imeHint && <p className="mt-4 text-sm text-accent">{t("learner:typing_ime_hint")}</p>}
      </main>
    </div>
  );
}

// ── 完了 ─────────────────────────────────────────────

function TypingComplete({ mode, prompts, finished, onAgain }: {
  mode: Mode; prompts: string[]; finished: Finished; onAgain: (prompts: string[]) => void;
}) {
  const { t } = useTranslation(["common", "learner"]);
  const queryClient = useQueryClient();
  const [result, setResult] = useState<SessionResult | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [againLoading, setAgainLoading] = useState(false);
  const sent = useRef(false);

  const accuracy = accuracyPercent(finished.totalKeys, finished.correctKeys);
  const cpm = charsPerMinute(prompts, finished.durationMs);

  const save = useCallback(async () => {
    setSaveError(false);
    try {
      const data = await api<SessionResult>("/api/learner/typing/sessions", {
        method: "POST",
        body: {
          mode,
          prompts,
          total_keys: finished.totalKeys,
          correct_keys: finished.correctKeys,
          duration_ms: finished.durationMs,
        },
      });
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    } catch {
      sent.current = false;
      setSaveError(true);
    }
  }, [mode, prompts, finished, queryClient]);

  // 1回の練習につき1度だけ送る（StrictMode の二重実行でも二重に記録しない）
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    void save();
  }, [save]);

  async function again() {
    if (mode === "custom") { onAgain(prompts); return; }
    setAgainLoading(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["typing-prompts", mode],
        queryFn: () => fetchPrompts(mode),
        staleTime: 0,
      });
      const usable = data.prompts.filter((p) => parsePromptQuery(p) !== null);
      onAgain(usable.length > 0 ? usable : prompts);
    } catch {
      onAgain(prompts);
    } finally {
      setAgainLoading(false);
    }
  }

  return (
    <div
      data-testid="typing-complete"
      className="relative w-full min-h-screen bg-gradient-to-br from-sakura/20 via-white to-blue-50 flex flex-col items-center justify-center px-4 py-8 overflow-hidden"
    >
      <SakuraField />
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 text-2xl sm:text-3xl font-bold text-primary mb-8 text-center"
      >
        {t("learner:typing_complete_title")}
      </motion.h1>

      <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-2xl mb-8">
        <Stat label={t("learner:earned_xp_label")} accent="text-yellow-600">
          <span data-testid="typing-xp">{result ? `+${result.xp_awarded} XP` : "…"}</span>
        </Stat>
        <Stat label={t("learner:coin_label")} accent="text-pink-600">
          {result ? `+${result.coin_awarded}` : "…"}
        </Stat>
        <Stat label={t("learner:typing_accuracy")} accent="text-green-600" sub={`${finished.correctKeys}/${finished.totalKeys}`}>
          <span data-testid="typing-accuracy">{accuracy}%</span>
        </Stat>
        <Stat label={t("learner:typing_cpm")} accent="text-primary">
          {cpm}
        </Stat>
      </div>

      {!result && !saveError && <p className="relative z-10 text-sm text-muted mb-4">{t("learner:typing_saving")}</p>}
      {saveError && (
        <div className="relative z-10 flex flex-col items-center gap-2 mb-4">
          <p className="text-sm text-accent">{t("learner:typing_save_error")}</p>
          <button
            type="button"
            onClick={() => { sent.current = true; void save(); }}
            className="text-sm font-bold text-primary underline"
          >
            {t("learner:typing_save_retry")}
          </button>
        </div>
      )}

      <div className="relative z-10 w-full max-w-md flex flex-col gap-3">
        <button
          type="button"
          onClick={again}
          disabled={againLoading}
          className="w-full bg-primary text-white font-bold py-3 rounded-2xl shadow hover:bg-primary/90 transition text-base sm:text-lg disabled:opacity-50"
        >
          {againLoading ? t("common:loading") : t("learner:typing_again")}
        </button>
        <Link
          to="/"
          className="w-full bg-white border-2 border-gray-200 text-primary font-bold py-3 rounded-2xl shadow-sm hover:border-primary/60 transition text-base sm:text-lg text-center"
        >
          {t("learner:back_to_map")}
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, accent, sub, children }: { label: string; accent: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-md px-3 py-4 flex flex-col items-center text-center">
      <div className={`text-xl sm:text-2xl font-bold ${accent} leading-tight`}>{children}</div>
      {sub && <div className="text-[10px] sm:text-xs text-muted leading-tight">{sub}</div>}
      <div className="text-[10px] sm:text-xs text-muted mt-1">{label}</div>
    </div>
  );
}
